import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaShieldAlt, FaEye, FaSearch, FaTimes, FaInbox,
  FaSync, FaCalendarAlt, FaFileContract, FaInfoCircle, FaCheckCircle
} from "react-icons/fa";

const CandidateDocuments = () => {
  const { appliedJobs, searchQuery: globalSearchQuery, setIsModalOpen, setSelectedOffer, setActiveDocTab } = useOutletContext();
  const [localSearchQuery, setLocalSearchQuery] = useState("");

  const agreementApps = appliedJobs.filter(app => {
    // Show if admin sent agreement text
    const hasAgreementText = app.adminAgreements && String(app.adminAgreements).trim().length > 0;
    // Show if admin attached a file
    const hasAttachment = app.adminAttachment && String(app.adminAttachment).trim().length > 0;
    // Show if there's a documentHistory entry of type "Agreement"
    const hasDocHistory = app.documentHistory && app.documentHistory.some(d =>
      d.documentType === "Agreement" || d.documentType === "agreement"
    );
    return hasAgreementText || hasAttachment || hasDocHistory;
  }).filter(app => {
    const combinedQuery = (globalSearchQuery || localSearchQuery).toLowerCase();
    return (app.jobId?.role || "").toLowerCase().includes(combinedQuery);
  });

  return (
    <div className="w-full h-full">
      
      {/* ── Filters Bar ── */}
      <div className="p-3 mb-3 bg-white rounded-lg shadow-md">
        <div className="flex flex-wrap items-center gap-2">
          
          <div className="flex items-center gap-2 mr-auto pr-4">
             <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-blue-600 flex items-center justify-center text-white shadow-md">
                <FaShieldAlt size={14} />
             </div>
             <div>
                <h2 className="text-sm font-black text-gray-800 tracking-tight leading-none">AGREEMENTS</h2>
                <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mt-0.5">Employment Contracts</div>
             </div>
          </div>

          <div className="relative flex-1 min-w-[150px] max-w-sm">
            <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder="Search by role..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs font-bold border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 outline-none uppercase tracking-wider"
            />
          </div>

          {localSearchQuery && (
            <button
              onClick={() => setLocalSearchQuery("")}
              className="h-7 px-3 text-[10px] font-bold text-gray-600 bg-gray-100 border border-gray-200 rounded hover:bg-gray-200 transition uppercase tracking-wider"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Table Area ── */}
      <div className="p-0 bg-white border shadow-lg rounded-xl overflow-hidden">
        {agreementApps.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="text-xs text-left text-white bg-gradient-to-r from-green-500 to-blue-600">
                <tr>
                  <th className="py-2 px-4 text-center">Agreement Details</th>
                  <th className="py-2 px-4 text-center">Agreement ID</th>
                  <th className="py-2 px-4 text-center">Last Updated</th>
                  <th className="py-2 px-4 text-center">Status</th>
                  <th className="py-2 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {agreementApps.map((app) => (
                  <tr key={app._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-center">
                      <div className="text-xs font-bold text-gray-800">{app.jobId?.role || "Position Detail"}</div>
                      <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mt-0.5 flex items-center justify-center gap-1">
                        <FaFileContract className="text-indigo-400 text-[8px]" /> EMPLOYMENT CONTRACT
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-black text-[10px] text-gray-400 uppercase tracking-widest">
                        #{app._id.slice(-8).toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-600">
                        <FaCalendarAlt className="text-gray-400 text-[9px]" />
                        {new Date(app.updatedAt).toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: '2-digit' })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-[10px] font-black text-emerald-600 uppercase tracking-wider flex items-center justify-center gap-1.5">
                        <FaCheckCircle className="text-[11px]" /> VERIFIED
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center">
                        <button
                          onClick={() => { setSelectedOffer(app); setActiveDocTab("agreement"); setIsModalOpen(true); }}
                          className="text-indigo-600 hover:text-indigo-800 transition-all active:scale-90"
                          title="View Document"
                        >
                          <FaEye size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-300">
              <FaInbox size={20} />
            </div>
            <h3 className="text-sm font-black text-gray-700 mb-1">No Agreements Found</h3>
            <p className="text-[10px] text-gray-400 font-medium tracking-wider">Your signed contracts and official agreements will appear here once finalized.</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest pt-6 pb-2">
        <FaInfoCircle className="text-blue-400" />
        Timely Health Recruitment Compliance • {new Date().getFullYear()}
      </div>
    </div>
  );
};

export default CandidateDocuments;